import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TokenDto } from './dto/user/token-dto.model';
import { UserDto } from './dto/user/user-dto.model';
import { BoothDto } from './dto/user/booth-dto.model';
import { StationDto } from './dto/user/station-dto.model';
import { ResetPasswordDto } from './dto/reset-password-dto.model';
import { VerifyEmailDto } from './dto/user/verify-email-dto.model';
import { CenterDto } from './dto/user/center-dto.model';
import {
  SocialAuthProvider,
  SocialAuthResponseDto,
} from './dto/user/social-auth-dto.model';

const API_USERS_URL = `${environment.apiUrl}user/`;
const API_STATIONS_URL = `${API_USERS_URL}booth/`;
const API_CREATE_URL = `${API_USERS_URL}create/`;
const API_UPLOAD_IMAGE = `${API_USERS_URL}center/upload-image/`;

export interface CenterImageUploadResponse {
  id: number;
  image: string;
}

export interface DiscourseConnectResponse {
  redirect_url: string;
}

export interface LegalDocumentPayload {
  document_type: string;
  version: number;
  effective_at: string;
  requires_acceptance: boolean;
  content_hash: string;
  content: {
    title: string;
    sections: Array<{
      title?: string;
      blocks: Array<{type: 'paragraph' | 'bullets'; text?: string; items?: string[]}>;
    }>;
  };
}

export type CurrentLegalDocumentsResponse = Record<string, LegalDocumentPayload>;

@Injectable({
  providedIn: 'root',
})
export class UserHTTPService {
  constructor(private http: HttpClient) {}

  login(email: string, password: string): Observable<TokenDto> {
    return this.http.post<TokenDto>(`${API_USERS_URL}token/`, {
      email,
      password,
    });
  }

  socialLogin(
    provider: SocialAuthProvider,
    accessToken: string,
  ): Observable<SocialAuthResponseDto> {
    return this.http.post<SocialAuthResponseDto>(
      `${API_USERS_URL}social-auth/`,
      { provider, access_token: accessToken },
    );
  }

  createBooth(temp_token: string, name: string): Observable<BoothDto> {
    return this.http.post<BoothDto>(`${API_USERS_URL}booth/`, {
      temp_token,
      name,
    });
  }

  getUser(): Observable<UserDto> {
    return this.http.get<UserDto>(`${API_USERS_URL}me/`);
  }

  createUser(user: UserDto): Observable<UserDto> {
    return this.http.post<UserDto>(API_CREATE_URL, user);
  }

  forgotPassword(email: string, language: string): Observable<boolean> {
    return this.http.post<boolean>(`${API_USERS_URL}forgot-password/`, {
      email,
    }, { params: { language: language.split('-')[0] } });
  }

  resetPassword(resetPasswordDto: ResetPasswordDto): Observable<ResetPasswordDto> {
    return this.http.post<ResetPasswordDto>(
      `${API_USERS_URL}reset-password/`,
      resetPasswordDto
    );
  }

  verifyEmail(verifyEmail: VerifyEmailDto): Observable<VerifyEmailDto> {
    return this.http.post<VerifyEmailDto>(
      `${API_USERS_URL}verify-email/`,
      verifyEmail
    );
  }

  getStationList(): Observable<StationDto[]> {
    return this.http.get<StationDto[]>(API_STATIONS_URL);
  }

  uploadImage(file: File): Observable<CenterImageUploadResponse> {
    const formData = new FormData();
    formData.append('image', file, file.name);
    return this.http.patch<CenterImageUploadResponse>(API_UPLOAD_IMAGE, formData);
  }
  
  

  patchCenter(center: object): Observable<CenterDto> {
    return this.http.patch<CenterDto>(`${API_USERS_URL}center/`, center);
  }

  patchUserProfile(user: object): Observable<UserDto> {
    return this.http.patch<UserDto>(`${API_USERS_URL}me/`, user);
  }

  verifyToken(boothToken: string): Observable<boolean> {
    return this.http.get<boolean>(`${API_USERS_URL}verify-temp-booth-token/?token=${boothToken}`, {
      observe: 'response',
    }).pipe(
      map(response => response.status === 200),
      catchError(() => of(false))
    );
  }

  getCurrentLegalDocuments(language = 'en'): Observable<CurrentLegalDocumentsResponse> {
    return this.http.get<CurrentLegalDocumentsResponse>(
      `${API_USERS_URL}legal-documents/current/`, { params: { language } }
    );
  }

  acceptLegalDocuments(acceptances: Array<{
    document_type: string; version: number; content_hash: string;
  }>): Observable<unknown> {
    return this.http.post(`${API_USERS_URL}legal-documents/accept/`, { acceptances });
  }

  getDiscourseConnectRedirect(
    sso: string,
    sig: string
  ): Observable<DiscourseConnectResponse> {
    return this.http.get<DiscourseConnectResponse>(
      `${API_USERS_URL}discourse-connect/`,
      {
        params: { sso, sig },
      }
    );
  }

  deleteStation(stationId: string): Observable<void> {
    return this.http.delete<void>(`${API_STATIONS_URL}${stationId}/`);
  }

  patchRenameStation(stationId: string, name: string): Observable<StationDto> {
    return this.http.patch<StationDto>(`${API_STATIONS_URL}${stationId}/`, { name });
  }
}
